import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, FileText } from "lucide-react";
import { navLinks, company } from "../../mock";
import { useRFQ } from "../../context/RFQContext";
import { Button } from "../ui/button";
import Logo from "../Logo";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openRFQ, enquiry } = useRFQ();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#0b1b2b]/90 backdrop-blur-md border-b border-border shadow-[0_1px_20px_rgba(12,97,82,0.06)]" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group">
          <span className="h-10 w-10 rounded-xl bg-[#06111d] flex items-center justify-center shadow-sm">
            <Logo size={28} />
          </span>
          <span className="leading-none">
            <span className="block font-display text-[19px] text-foreground tracking-tight">Novocreste</span>
            <span className="block text-[10px] uppercase tracking-[0.22em] text-[#2ED9B8]">International</span>
          </span>
        </Link>

        <ul className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? "text-[#2ED9B8]"
                      : "text-muted-foreground hover:text-[#2ED9B8]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          {enquiry.length > 0 && (
            <Link
              to="/products"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-[#2ED9B8] px-2.5 py-1.5 rounded-full bg-[#0f2236] border border-white/10"
            >
              Enquiry
              <span className="h-4 min-w-4 px-1 rounded-full bg-[#00ab96] text-white text-[10px] flex items-center justify-center">
                {enquiry.length}
              </span>
            </Link>
          )}
          <Button
            onClick={openRFQ}
            className="hidden sm:inline-flex bg-[#d9a441] hover:bg-[#c8942f] text-[#0c1626] font-semibold rounded-full h-10 px-5 text-[13.5px] shadow-[0_8px_20px_-8px_rgba(217,164,65,0.7)]"
          >
            <FileText className="h-4 w-4 mr-1.5" /> Request an RFQ
          </Button>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden h-10 w-10 rounded-lg border border-border flex items-center justify-center text-foreground"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-300 bg-card border-b border-border ${
          mobileOpen ? "max-h-[520px]" : "max-h-0"
        }`}
      >
        <ul className="px-5 py-3 space-y-1">
          {navLinks.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                className={({ isActive }) =>
                  `block px-3 py-2.5 rounded-lg text-[15px] font-medium ${
                    isActive ? "bg-[#0f2236] text-[#2ED9B8]" : "text-muted-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
          <li className="pt-2">
            <Button
              onClick={() => {
                setMobileOpen(false);
                openRFQ();
              }}
              className="w-full bg-[#d9a441] hover:bg-[#c8942f] text-[#0c1626] font-semibold rounded-full h-11"
            >
              <FileText className="h-4 w-4 mr-1.5" /> Request an RFQ
            </Button>
          </li>
        </ul>
      </div>
    </header>
  );
}
